import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { NAME_MAX, type SellerCreatedResponse } from '@sellit/shared';
import { platform } from '../lib/api';
import { setAppTitle } from '../lib/format';
import { errorText, fieldErrors, SetupLinkCard, useLoad } from './common';

export function SellerNewPage() {
  const [params] = useSearchParams();
  const requestId = params.get('request') ?? undefined;
  const [form, setForm] = useState({ displayName: '', email: '', sendEmail: true });
  const [saleName, setSaleName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [created, setCreated] = useState<SellerCreatedResponse | null>(null);
  const { data: overview } = useLoad(() => platform.overview());
  const emailEnabled = overview?.emailEnabled ?? false;
  useEffect(() => setAppTitle('New seller'), []);
  // Coming from a Start Selling request: fill in what they gave us.
  useEffect(() => {
    if (!requestId) return;
    platform.sellerRequest(requestId).then((r) => {
      setForm((f) => ({ ...f, displayName: r.name, email: r.email }));
      setSaleName(r.saleName);
    }, () => undefined);
  }, [requestId]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});
    try {
      setCreated(await platform.createSeller({ ...form, sendEmail: form.sendEmail && emailEnabled, requestId }));
    } catch (err) {
      setError(errorText(err));
      setFields(fieldErrors(err));
    } finally {
      setBusy(false);
    }
  }

  if (created) {
    return (
      <main className="page narrow pa-page">
        <h1>{created.seller.displayName} is set up</h1>
        <SetupLinkCard link={created.setupLink} name={created.seller.displayName} />
        <div className="stack">
          <Link className="btn primary big" to={`/platform-admin/sales/new?seller=${created.seller.id}${saleName ? `&title=${encodeURIComponent(saleName)}` : ''}`}>Create a sale for {created.seller.displayName}</Link>
          <Link className="btn" to={`/platform-admin/sellers/${created.seller.id}`}>View seller</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="page narrow pa-page">
      <header className="admin-header">
        <h1>New seller</h1>
        <Link className="link small" to="/platform-admin/sellers">Cancel</Link>
      </header>
      {requestId && <p className="notice small">From a Start Selling request. Check the details, then create the account.</p>}
      <p className="muted small">They'll get a one-time link to choose their own password. No password is ever sent.</p>
      <form className="stack" onSubmit={submit}>
        <label className="field">
          <span>Name</span>
          <input value={form.displayName} maxLength={NAME_MAX} autoComplete="off" onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
          {fields.displayName && <small className="error">{fields.displayName}</small>}
        </label>
        <label className="field">
          <span>Email (their login)</span>
          <input type="email" inputMode="email" autoComplete="off" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          {fields.email && <small className="error">{fields.email}</small>}
        </label>
        {emailEnabled && (
          <label className="check">
            <input type="checkbox" checked={form.sendEmail} onChange={(e) => setForm({ ...form, sendEmail: e.target.checked })} />
            <span>Email them the setup link</span>
          </label>
        )}
        {error && <p className="error" role="alert">{error}</p>}
        <button className="btn primary big" disabled={busy}>{busy ? 'Creating…' : 'Create seller'}</button>
      </form>
    </main>
  );
}
